import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, ExternalLink } from 'lucide-react';
import api from '../../utils/api';

const CATEGORY_COLORS = {
  'community':    'text-blue-400',
  'city council': 'text-red-400',
  'arts & culture': 'text-purple-400',
  'sport':        'text-green-400',
  'environment':  'text-emerald-400',
  'health':       'text-pink-400',
  'business':     'text-yellow-400',
  'other':        'text-gray-400',
};

const EventsWidget = ({ ward }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const params = ward ? `?ward=${ward}&upcoming_only=true` : '?upcoming_only=true';
        const res = await api.get(`/events${params}`);
        setEvents((res.data?.events || []).slice(0, 5));
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [ward]);

  return (
    <div className="bg-[#111] border border-[#1a1a1a] rounded p-4 mb-4" data-testid="events-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm text-[#C5A52A] tracking-wider">EXPERIENCE REGINA</h3>
        <Calendar className="w-4 h-4 text-[#C5A52A]" />
      </div>

      {loading ? (
        <div className="py-4 text-center">
          <div className="w-4 h-4 border border-gray-700 border-t-[#C5A52A] rounded-full animate-spin mx-auto"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="py-6 border-2 border-dashed border-[#1a1a1a] rounded text-center">
          <Calendar className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="font-mono text-xs text-gray-500">No upcoming events</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(ev => {
            const date = new Date(ev.event_date + 'T12:00:00');
            const monthStr = date.toLocaleDateString('en-CA', { month: 'short' }).toUpperCase();
            const dayNum = date.getDate();
            const catColor = CATEGORY_COLORS[ev.category] || 'text-gray-400';

            return (
              <div key={ev.id} className="flex items-start gap-2.5 group">
                {/* Mini date badge */}
                <div className="flex-shrink-0 text-center w-10 bg-[#0a0a0a] border border-[#1a1a1a] rounded p-1">
                  <div className="text-[9px] text-gray-500 font-mono leading-none">{monthStr}</div>
                  <div className="text-base font-black text-white leading-tight">{dayNum}</div>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0 py-0.5">
                  <div className="flex items-start justify-between gap-1">
                    <p className="font-mono text-xs text-gray-200 leading-snug line-clamp-2 group-hover:text-white transition-colors">
                      {ev.title}
                    </p>
                    {ev.url && (
                      <a href={ev.url} target="_blank" rel="noreferrer"
                        className="flex-shrink-0 text-gray-600 hover:text-[#C5A52A] transition-colors mt-0.5">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {ev.event_time && (
                      <span className="text-[10px] font-mono text-gray-600">{ev.event_time}</span>
                    )}
                    {ev.location && (
                      <span className="text-[10px] font-mono text-gray-600 flex items-center gap-0.5 truncate">
                        <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                        <span className="truncate">{ev.location}</span>
                      </span>
                    )}
                    <span className={`text-[9px] font-mono uppercase tracking-wide ${catColor} ml-auto flex-shrink-0`}>{ev.category}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
        <p className="font-mono text-[10px] text-gray-600 text-center">Upcoming community events</p>
      </div>
    </div>
  );
};

export default EventsWidget;
